import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dropdown, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { apiHandler } from '../../../api/apihandler';
import {
  FEEDS_LIST,
  FEEDS_DETAIL,
  FEED_LIKE,
  FEED_LIKES,
  FEED_DELETE,
  ADD_FAVORITE_RECIPE,
  FEED_MADE_IT_POST,
  FEED_MADE_IT_MEDIA_POST,
  FEED_MADE_IT_LIST,
  FEED_MADE_IT_LIKE,
  STORIES_LIST,
  STORIES_VIEW,
  STORIES_VIEWS,
  REPORT_FLAGS_LIST,
  POST_FLAG,
  LATEST_CHALLENGES,
  FOLLOWER_LIST,
  FOLLOWING_LIST,
  PEOPLE_YOU_MAY_KNOW,
  USER_FOLLOW_REQUEST,
} from '../../../api/constants';
import { handleImageUpload } from '../../../api/uploads';
import { user_data as userDataSelector } from '../../../store/setting/selectors.ts';
import CommentList from '../../../components/comment/commentList';
import CommentBox from '../../../components/comment/commentBox';
import MadeItCommentList from '../../../components/madeIt/madeItCommentList';
import MadeItCommentBox from '../../../components/madeIt/madeItCommentBox';
import CreatePostModal from '../../../components/createPost/createPostModal';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import AdFeedCard from '../../../components/ads/AdFeedCard';
import AdRailWidget from '../../../components/ads/AdRailWidget';
import {
  getCurrentUser,
  getAvatarColor,
  getInitials,
  formatRelativeTime,
  apiSucceeded,
  apiErrorMessage,
} from '../../../views/dashboard/feed/feedUtils';
import './home-app-page.css';

export const formatTimeLeft = (dateStr) => {
  if (!dateStr) return null;
  const diffMs = new Date(dateStr).getTime() - Date.now();
  if (diffMs <= 0) return null;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${Math.max(diffMin, 1)}m left`;
  if (diffMin < 2880) return `${Math.floor(diffMin / 60)}h left`;
  return `${Math.floor(diffMin / 1440)}d left`;
};

export const REACTION_OPTIONS = [
  { key: 'Nailed it', value: 1, icon: '🎯' },
  { key: 'Almost', value: 2, icon: '🤏' },
  { key: 'My twist', value: 3, icon: '🌀' },
  { key: 'Tips', value: 4, icon: '💡' },
];

export const Avatar = ({ url, name, className, style }) => {
  const fill = getAvatarColor(name || 'User');
  if (url) {
    return <img src={url} alt={name} className={className} style={style} />;
  }
  return (
    <div className={className} style={{ background: fill, ...style }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

const StoryBubble = ({ name, avatarUrl, seen, onPress, isAdd, onAddPress }) => {
  if (isAdd) {
    return (
      <button type="button" className="nmha-story" onClick={onPress}>
        <div className="nmha-story-add">+</div>
        <span className="nmha-story-name">{name}</span>
      </button>
    );
  }
  return (
    <div className="nmha-story">
      <button type="button" className="nmha-story-ring-btn" onClick={onPress}>
        <div className={`nmha-story-ring${seen ? ' seen' : ''}`}>
          <Avatar url={avatarUrl} name={name} className="nmha-story-avatar" />
        </div>
        {onAddPress && (
          <span
            className="nmha-story-add-badge"
            role="button"
            tabIndex={0}
            aria-label={`Add to ${name}`}
            onClick={(e) => { e.stopPropagation(); onAddPress(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onAddPress(); }
            }}
          >
            +
          </span>
        )}
      </button>
      <span className="nmha-story-name">{name}</span>
    </div>
  );
};

const STORY_SLIDE_MS = 5000;

// Groups raw STORIES_LIST post rows by user, flattening each post's
// user_post_images into individual slides - story-view is tracked per
// image_id, so each image (not each post) is one slide in the viewer.
const buildStoryGroups = (rawList) => {
  const byUser = new Map();
  rawList.forEach((d) => {
    const userId = d?.user_id ?? d?.user?.id;
    if (!userId) return;
    if (!byUser.has(userId)) {
      byUser.set(userId, {
        userId,
        name: `${d?.user?.first_name || ''} ${d?.user?.last_name || ''}`.trim() || 'User',
        avatarUrl: d?.user?.user_setting?.photo || null,
        items: [],
      });
    }
    const group = byUser.get(userId);
    (d?.user_post_images || []).forEach((img) => {
      const isVideo = img?.media_type === 1;
      group.items.push({
        id: String(img.id),
        postId: d.id,
        image: isVideo ? (img.thumb || img.image_path) : img.image_path,
        videoUrl: isVideo ? img.image_path : null,
        isVideo,
        createdAt: d.created_at,
      });
    });
  });
  return Array.from(byUser.values()).filter((g) => g.items.length > 0);
};

// Full-screen Instagram-style story viewer: segmented per-item progress
// bars that auto-advance (images on a timer, videos on their own
// duration), tap-left/right zones to step back/forward across items and
// user groups, hold-to-pause, and - for the signed-in user's own group -
// a "Seen by" list fetched lazily per item.
const StoryViewerOverlay = ({ groups, initialGroupIndex, currentUserId, onClose, onItemViewed }) => {
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [itemIndex, setItemIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [viewers, setViewers] = useState(null);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const videoRef = useRef(null);

  const group = groups[groupIndex];
  const item = group?.items?.[itemIndex];
  const isOwn = group?.userId === currentUserId;

  const goNext = () => {
    if (!group) return;
    if (itemIndex < group.items.length - 1) {
      setItemIndex((i) => i + 1);
    } else if (groupIndex < groups.length - 1) {
      setGroupIndex((g) => g + 1);
      setItemIndex(0);
    } else {
      onClose();
    }
  };

  const goPrev = () => {
    if (!group) return;
    if (itemIndex > 0) {
      setItemIndex((i) => i - 1);
    } else if (groupIndex > 0) {
      const prevGroup = groups[groupIndex - 1];
      setGroupIndex((g) => g - 1);
      setItemIndex(prevGroup.items.length - 1);
    }
  };

  useEffect(() => {
    setShowViewers(false);
    setViewers(null);
    setVideoProgress(0);
    if (item) onItemViewed?.(item.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIndex, itemIndex]);

  useEffect(() => {
    if (!item?.isVideo || !videoRef.current) return;
    if (paused) videoRef.current.pause();
    else videoRef.current.play().catch(() => {});
  }, [paused, item]);

  async function loadViewers() {
    if (!item) return;
    setShowViewers(true);
    setViewersLoading(true);
    try {
      const res = await apiHandler(STORIES_VIEWS, 'POST', { image_id: item.id });
      if (apiSucceeded(res)) {
        const list =
          res?.data?.data?.storyViews ||
          res?.data?.data?.viewers ||
          res?.data?.data?.userViews ||
          res?.data?.data?.views?.data ||
          res?.data?.data?.views ||
          [];
        setViewers(list);
      } else {
        setViewers([]);
      }
    } catch (e) {
      console.error(e);
      setViewers([]);
    } finally {
      setViewersLoading(false);
    }
  }

  if (!group || !item) return null;

  return (
    <div className="nmha-story-viewer">
      <div className="nmha-story-viewer-bars">
        {group.items.map((it, i) => {
          const isActive = i === itemIndex;
          const isActiveVideo = isActive && it.isVideo;
          return (
            <div key={it.id} className="nmha-story-bar">
              <div
                className={`nmha-story-bar-fill${i < itemIndex ? ' full' : ''}${isActive && !it.isVideo ? ' active' : ''}`}
                style={
                  isActive && !it.isVideo
                    ? { animationDuration: `${STORY_SLIDE_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }
                    : isActiveVideo
                      ? { width: `${videoProgress * 100}%` }
                      : undefined
                }
                onAnimationEnd={isActive && !it.isVideo ? goNext : undefined}
              />
            </div>
          );
        })}
      </div>

      <div className="nmha-story-viewer-header">
        <Avatar url={group.avatarUrl} name={group.name} className="nmha-story-viewer-avatar" />
        <div>
          <div className="nmha-story-viewer-name">{group.name}</div>
          <div className="nmha-story-viewer-time">{formatRelativeTime(item.createdAt)}</div>
        </div>
        <button type="button" className="nmha-story-viewer-close" onClick={onClose}>✕</button>
      </div>

      <div className="nmha-story-viewer-media">
        {item.isVideo ? (
          <video
            ref={videoRef}
            src={item.videoUrl}
            autoPlay
            playsInline
            muted={false}
            onEnded={goNext}
            onTimeUpdate={(e) => {
              const { currentTime, duration } = e.currentTarget;
              if (duration) setVideoProgress(currentTime / duration);
            }}
          />
        ) : (
          <img src={item.image} alt="" />
        )}
      </div>

      <div
        className="nmha-story-tap-zone left"
        onClick={goPrev}
        onMouseDown={() => setPaused(true)}
        onMouseUp={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      />
      <div
        className="nmha-story-tap-zone right"
        onClick={goNext}
        onMouseDown={() => setPaused(true)}
        onMouseUp={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      />

      {isOwn && (
        <div className="nmha-story-viewers-bar">
          <button type="button" className="nmha-story-viewers-btn" onClick={loadViewers}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>visibility</span>
            Seen by{viewers ? ` ${viewers.length}` : ''}
          </button>
        </div>
      )}

      {showViewers && (
        <div className="nmha-story-viewers-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="nmha-story-viewers-sheet-head">
            <b>Seen by</b>
            <button type="button" onClick={() => setShowViewers(false)}>✕</button>
          </div>
          {viewersLoading ? (
            <p className="nmha-loading-text">Loading…</p>
          ) : !viewers || viewers.length === 0 ? (
            <p className="nmha-empty-text">No views yet.</p>
          ) : viewers.map((v, i) => {
            const name = `${v?.user?.first_name || ''} ${v?.user?.last_name || ''}`.trim() || 'User';
            return (
              <div key={v?.id ?? i} className="nmha-story-viewer-row">
                <Avatar url={v?.user?.user_setting?.photo} name={name} className="nmha-pr-avatar" />
                <div className="nmha-pr-name">{name}</div>
                <span className="nmha-story-viewer-row-time">{formatRelativeTime(v?.viewed_at || v?.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PostCard = ({
  post, onOpenRecipe, onProfilePress, onLikePress, onLikesPress,
  onCommentsPress, onMadeItPress, onMadeItCountPress, onSharePress, onFavoritePress, onDelete, onFlagged,
}) => {
  const metaLine = [post.region, post.time].filter(Boolean).join(' · ');
  const [reportFlags, setReportFlags] = useState([]);
  const [showFlagModal, setShowFlagModal] = useState(false);

  async function openReportFlags() {
    try {
      const res = await apiHandler(REPORT_FLAGS_LIST, 'POST');
      if (apiSucceeded(res)) {
        setReportFlags(res?.data?.data?.reportFlags || []);
        setShowFlagModal(true);
      }
    } catch (e) { console.error(e); }
  }

  async function flagPost(flagId) {
    try {
      const res = await apiHandler(POST_FLAG, 'POST', { post_id: post.originalId, report_flag_id: flagId });
      setShowFlagModal(false);
      if (apiSucceeded(res)) {
        Swal.fire('Reported', "Thanks — we'll review this post.", 'success');
        onFlagged?.();
      } else {
        Swal.fire('Error', apiErrorMessage(res, 'Unable to submit report.'), 'error');
      }
    } catch (e) { console.error(e); }
  }

  const confirmDelete = () => {
    Swal.fire({
      title: 'Delete post?',
      text: 'This will remove the post from your profile.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    }).then((result) => { if (result.isConfirmed) onDelete?.(); });
  };

  return (
    <article className="nmha-post-card">
      <div className="nmha-post-top">
        <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }} onClick={onProfilePress}>
          <Avatar url={post.avatarUrl} name={post.author} className="nmha-post-avatar" />
          <div className="nmha-post-who"><b>{post.author}</b><span>{metaLine}</span></div>
        </button>
        <Dropdown onClick={(e) => e.stopPropagation()} style={{ marginLeft: 'auto' }}>
          <Dropdown.Toggle as="span" bsPrefix="nmha-post-more" style={{ cursor: 'pointer' }}>⋯</Dropdown.Toggle>
          <Dropdown.Menu align="end">
            {post.isOwn ? (
              <Dropdown.Item onClick={confirmDelete}>Delete post</Dropdown.Item>
            ) : (
              <Dropdown.Item onClick={openReportFlags}>Report post</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="nmha-post-thumb" onClick={onOpenRecipe} role="button" tabIndex={0}>
        {post.isVideo && post.videoUrl ? (
          <video
            src={post.videoUrl}
            poster={post.mediaUrl || undefined}
            controls
            playsInline
            preload="metadata"
            onClick={(e) => e.stopPropagation()}
          />
        ) : post.mediaUrl ? (
          <img src={post.mediaUrl} alt="" />
        ) : (
          <MediaPlaceholderIcon size={130} />
        )}
        {post.isChallengeEntry && <span className="nmha-challenge-flag">⚑ Challenge entry</span>}
      </div>

      <div className="nmha-post-body">
        {post.title ? <p className="nmha-post-title" onClick={onOpenRecipe} role="button" tabIndex={0}>{post.title}</p> : null}
        {post.content ? <p className="nmha-post-desc">{post.content}</p> : null}
        <div className="nmha-post-actions">
          <span className={`nmha-pa-btn${post.isLiked ? ' liked' : ''}`} onClick={onLikePress} role="button" tabIndex={0}>
            <span className={`material-symbols-outlined${post.isLiked ? ' filled' : ''}`} style={{ fontSize: 18 }}>favorite</span>
            <span onClick={(e) => { e.stopPropagation(); onLikesPress(); }}>{post.likes}</span>
          </span>
          <span className="nmha-pa-btn" onClick={onCommentsPress} role="button" tabIndex={0}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chat_bubble_outline</span> {post.comments}
          </span>
          <span className="nmha-pa-btn" onClick={(e) => { e.stopPropagation(); onMadeItCountPress(); }} role="button" tabIndex={0}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>skillet</span> {post.madeItCount} made it
          </span>
          <span className="nmha-pa-btn" onClick={onSharePress} role="button" tabIndex={0}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>share</span> Share
          </span>
          <span className={`nmha-pa-btn nmha-pa-save${post.isFavorite ? ' liked' : ''}`} onClick={(e) => { e.stopPropagation(); onFavoritePress(); }} role="button" tabIndex={0}>
            <span className={`material-symbols-outlined${post.isFavorite ? ' filled' : ''}`} style={{ fontSize: 18 }}>bookmark</span> Save
          </span>
          <button type="button" className="nmha-made-pill-btn" onClick={onMadeItPress}>Made it?</button>
        </div>
      </div>

      <Modal show={showFlagModal} onHide={() => setShowFlagModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Report reason</Modal.Title></Modal.Header>
        <Modal.Body>
          {reportFlags.map((f) => (
            <div key={f?.id} style={{ padding: '10px 4px', cursor: 'pointer', borderBottom: '1px solid #eee' }} onClick={() => flagPost(f?.id)}>
              {f?.name}
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </article>
  );
};

export const MadeItEntryScreen = ({ post, onClose }) => {
  const [reaction, setReaction] = useState(REACTION_OPTIONS[0]);
  const [content, setContent] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaIsVideo, setMediaIsVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const clearMedia = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    setMediaFile(null);
    setMediaPreview(null);
    setMediaIsVideo(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setMediaFile(file);
    setMediaPreview(url);
    setMediaIsVideo(file.type.startsWith('video'));
  };

  const submitPost = async (shouldShare) => {
    if (!content.trim()) {
      Swal.fire('Required', 'Please add a note about your experience.', 'warning');
      return;
    }

    setIsLoading(true);
    setUploadStatus('Posting…');
    try {
      const res = await apiHandler(FEED_MADE_IT_POST, 'POST', {
        content: content.trim(),
        original_recipe_id: post?.originalId || post?.id,
        reaction: reaction.value,
      });

      if (!apiSucceeded(res)) {
        const rawMsg = apiErrorMessage(res, 'Failed to post. Please try again.');
        const isDuplicate = typeof rawMsg === 'string' && (rawMsg.includes('Duplicate entry') || rawMsg.includes('unique'));
        throw new Error(isDuplicate
          ? "You've already posted a 'Made It' for this recipe. Only one entry per recipe is allowed."
          : rawMsg);
      }

      const madeItId =
        res?.data?.data?.user_recipe_made_it?.id ||
        res?.data?.data?.made_it?.id ||
        res?.data?.data?.madeIt?.id ||
        res?.data?.data?.id;

      if (mediaFile && madeItId) {
        setUploadStatus(mediaIsVideo ? 'Uploading video…' : 'Uploading photo…');
        const s3Url = await handleImageUpload(mediaFile, mediaIsVideo ? 'feedVideos/' : 'feedImages/', mediaFile.type);

        setUploadStatus('Saving media…');
        const mediaRes = await apiHandler(FEED_MADE_IT_MEDIA_POST, 'POST', {
          user_recipe_made_it_id: madeItId,
          file_url: s3Url,
          file_type: mediaIsVideo ? 2 : 1,
          thumb: '',
        });

        if (!apiSucceeded(mediaRes)) {
          throw new Error(apiErrorMessage(mediaRes, 'Failed to save media.'));
        }
      }

      if (shouldShare) {
        const recipeTitle = post?.title || 'a recipe';
        const shareMessage = [
          `${reaction?.key}! I just made "${recipeTitle}" on Nemonto 🍜`,
          content.trim(),
          'Join me on Nemonto!',
        ].join('\n\n');

        if (navigator.share) {
          try { await navigator.share({ title: `I made "${recipeTitle}" on Nemonto!`, text: shareMessage }); } catch (_) { /* user cancelled share */ }
        } else {
          try {
            await navigator.clipboard.writeText(shareMessage);
            Swal.fire('Copied!', 'Share text copied to your clipboard.', 'success');
          } catch (_) { /* clipboard unavailable */ }
        }
        onClose();
      } else {
        Swal.fire('Posted!', 'Your "Made It" post has been shared to your feed.', 'success').then(onClose);
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setIsLoading(false);
      setUploadStatus('');
    }
  };

  return (
    <div>
      <div className="nmha-entry-header">
        <button type="button" onClick={onClose} disabled={isLoading} style={{ fontSize: 20 }}>✕</button>
        <h2 className="nmha-entry-title">Made it!</h2>
        <div style={{ width: 24 }} />
      </div>

      <div className="nmha-entry-wrap">
        {post ? (
          <div className="nmha-entry-ref-card">
            <div className="nmha-entry-ref-img-wrap">
              {post.mediaUrl ? <img src={post.mediaUrl} alt="" /> : <MediaPlaceholderIcon size={32} />}
            </div>
            <div>
              <p className="nmha-entry-ref-label">Making this recipe</p>
              <p className="nmha-entry-ref-title">{post.title || 'Untitled recipe'}</p>
              {post.author ? <p className="nmha-entry-ref-author">by {post.author}</p> : null}
            </div>
          </div>
        ) : null}

        <p className="nmha-entry-label">Your result</p>
        <input ref={fileInputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileChange} />
        <div className="nmha-entry-media-picker" onClick={() => !isLoading && !mediaFile && fileInputRef.current?.click()}>
          {mediaFile ? (
            <>
              {mediaIsVideo
                ? <video src={mediaPreview} className="nmha-entry-media-thumb" muted />
                : <img src={mediaPreview} alt="" className="nmha-entry-media-thumb" />}
              <div className="nmha-entry-media-overlay">
                <div className="nmha-entry-media-icon-circle">
                  <span className="material-symbols-outlined">{mediaIsVideo ? 'play_circle' : 'image'}</span>
                </div>
                <span className="nmha-entry-media-filename">{mediaFile.name}</span>
                <button type="button" className="nmha-entry-media-remove" onClick={(e) => { e.stopPropagation(); clearMedia(); }} disabled={isLoading}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="nmha-entry-media-icon-circle">
                <span className="material-symbols-outlined">add_photo_alternate</span>
              </div>
              <span className="nmha-entry-media-placeholder">Add your photo or video</span>
            </>
          )}
        </div>

        <p className="nmha-entry-label">Your note</p>
        <textarea
          className="nmha-entry-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="How did it turn out? Share your twist…"
          disabled={isLoading}
        />

        <p className="nmha-entry-label">How did it go?</p>
        <div className="nmha-entry-reaction-row">
          {REACTION_OPTIONS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`nmha-entry-reaction-card${reaction.key === item.key ? ' active' : ''}`}
              onClick={() => setReaction(item)}
              disabled={isLoading}
            >
              <span className="nmha-entry-reaction-icon" aria-hidden="true">{item.icon}</span>
              {item.key}
            </button>
          ))}
        </div>

        <div className="nmha-entry-footer">
          <button type="button" className="nmha-entry-post-btn" onClick={() => submitPost(false)} disabled={isLoading}>Post</button>
          <button type="button" className="nmha-entry-share-btn" onClick={() => submitPost(true)} disabled={isLoading}>Share</button>
        </div>
      </div>

      {isLoading && (
        <div className="nmha-overlay">
          <div className="nmha-overlay-card">
            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{uploadStatus || 'Posting…'}</p>
            <p style={{ fontSize: 12, color: '#9a8c84', margin: 0 }}>Don&apos;t close this screen</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const MadeItRecipeScreen = ({ post, onBackHome, onMadeItPress, onLikeChange, onFavoriteChange, onDetailLoaded, onMadeItCountPress }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(post?.isLiked || false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [favorited, setFavorited] = useState(post?.isFavorite || false);
  const [refreshCommentList, setRefreshCommentList] = useState(0);
  const [madeItTotal, setMadeItTotal] = useState(null);

  useEffect(() => {
    if (!post?.originalId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await apiHandler(FEEDS_DETAIL, 'POST', { post_id: post.originalId, user_id: post.userId, user_slug: null });
        if (apiSucceeded(res)) {
          const d = res.data.data.post;
          setDetail(d);
          setLiked(d?.is_like === 1);
          setLikeCount(d?.like_count || 0);
          if (d?.is_favorite !== undefined) setFavorited(d.is_favorite === 1);
          onDetailLoaded?.(d);
        }
      } catch (_) { /* keep list-derived fallback values */ }
      finally { setLoading(false); }
    })();
    // Made-it count is fetched from the made-it list itself (the source of
    // truth) rather than trusted off the recipe detail response, since that
    // response doesn't reliably carry an up-to-date made_it_count - this
    // also re-fires on every mount, so the count is fresh again right after
    // the user posts a new "Made it" and navigates back to this screen.
    (async () => {
      try {
        const res = await apiHandler(FEED_MADE_IT_LIST, 'POST', { page: 1, limit: 50, original_recipe_id: post.originalId });
        if (apiSucceeded(res)) {
          const meta = res?.data?.data?.meta;
          const list = res?.data?.data?.data || [];
          setMadeItTotal(meta?.total ?? list.length);
        }
      } catch (_) { /* keep detail/post-derived fallback count */ }
    })();
    // Deliberately keyed on the id, not the whole `post` object - some
    // callers can't guarantee a stable `post` reference across renders,
    // and re-running this fetch on every render would reset the
    // optimistic liked/favorited state mid-toggle (visible as a "blink").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.originalId]);

  const toggleLike = async () => {
    const next = !liked;
    const newCount = next ? likeCount + 1 : likeCount - 1;
    setLiked(next);
    setLikeCount(newCount);
    onLikeChange?.(next, newCount);
    try {
      await apiHandler(FEED_LIKE, 'POST', { post_id: post?.originalId });
    } catch (_) {
      setLiked(!next);
      setLikeCount(likeCount);
      onLikeChange?.(!next, likeCount);
    }
  };

  const toggleFavorite = async () => {
    const next = !favorited;
    setFavorited(next);
    onFavoriteChange?.(next);
    try {
      await apiHandler(ADD_FAVORITE_RECIPE, 'POST', { recipe_id: post?.originalId });
    } catch (_) {
      setFavorited(!next);
      onFavoriteChange?.(!next);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/pages/recipe/${post?.originalId || post?.id}`;
    const shareTitle = detail?.title || post?.title || 'Nemonto recipe';
    const shareData = { title: shareTitle, text: `Check out this recipe on Nemonto: ${shareTitle}`, url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (_) { /* user cancelled share */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        Swal.fire('Copied!', 'Link copied to your clipboard.', 'success');
      } catch (_) { /* clipboard unavailable */ }
    }
  };

  const title = detail?.title || post?.title || '';
  const content = detail?.content || post?.content || '';
  const cookTime = detail?.cook_time ?? post?.cookTime ?? 0;
  const servings = detail?.serving_for ?? post?.servings ?? 0;
  const calorieCount = detail?.calorie_count ?? 0;
  const madeItCount = madeItTotal ?? detail?.made_it_count ?? post?.madeItCount ?? 0;
  const commentCount = detail?.comment_count ?? post?.comments ?? 0;
  const ingredients = detail?.recipe_ingredients || [];
  const steps = [...(detail?.recipe_steps || [])].sort((a, b) => (a.step_number ?? 0) - (b.step_number ?? 0));
  const tips = detail?.recipe_tips || [];
  const cuisineTags = detail?.cuisine_tags || [];

  const authorFirstName = detail?.user?.first_name || '';
  const authorLastName = detail?.user?.last_name || '';
  const authorName = `${authorFirstName} ${authorLastName}`.trim() || post?.author || 'User';
  const authorPhoto = detail?.user?.user_setting?.photo || post?.avatarUrl || null;

  const firstMedia = detail?.user_post_images?.[0] || null;
  const heroIsVideo = firstMedia ? firstMedia.media_type === 1 : !!post?.isVideo;
  const heroImage = firstMedia ? (heroIsVideo ? (firstMedia.thumb || null) : firstMedia.image_path) : post?.mediaUrl || null;
  const heroVideoUrl = firstMedia ? (heroIsVideo ? firstMedia.image_path || null : null) : (post?.isVideo ? post?.videoUrl || null : null);

  return (
    <div>
      <div className="nmha-detail-back-row">
        <button type="button" className="nmha-back-btn" onClick={onBackHome}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span> Back
        </button>
      </div>

      <div className="nmha-detail-wrap">
        <div className="nmha-detail-author-row">
          <Avatar url={authorPhoto} name={authorName} className="nmha-detail-author-avatar" />
          <div>
            <div className="nmha-detail-author-name">{authorName}</div>
            <div className="nmha-detail-author-meta">Posted {formatRelativeTime(detail?.created_at || post?.time)}</div>
          </div>
        </div>

        <div className="nmha-detail-media-box">
          {heroIsVideo && heroVideoUrl ? (
            <video src={heroVideoUrl} poster={heroImage || undefined} controls playsInline preload="metadata" />
          ) : heroImage ? (
            <img src={heroImage} alt="" />
          ) : (
            <MediaPlaceholderIcon size={150} />
          )}
        </div>

        {title ? <h1 className="nmha-detail-title">{title}</h1> : null}
        {content ? <p className="nmha-detail-desc">{content}</p> : null}

        <div className="nmha-detail-action-bar">
          <button type="button" className={`nmha-detail-action${liked ? ' liked' : ''}`} onClick={toggleLike}>
            <span className={`material-symbols-outlined${liked ? ' filled' : ''}`} style={{ fontSize: 18 }}>favorite</span> {likeCount}
          </button>
          <span
            className="nmha-detail-action"
            onClick={() => document.getElementById('nmha-detail-comments')?.scrollIntoView({ behavior: 'smooth' })}
            role="button"
            tabIndex={0}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chat_bubble_outline</span> {commentCount}
          </span>
          <span className="nmha-detail-action" onClick={() => onMadeItCountPress?.()} role="button" tabIndex={0}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>skillet</span> {madeItCount} made it
          </span>
          <span className="nmha-detail-action" onClick={handleShare} role="button" tabIndex={0}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>share</span> Share
          </span>
          <span className={`nmha-detail-action${favorited ? ' liked' : ''}`} onClick={toggleFavorite} role="button" tabIndex={0}>
            <span className={`material-symbols-outlined${favorited ? ' filled' : ''}`} style={{ fontSize: 18 }}>bookmark</span> Save
          </span>
          <button type="button" className="nmha-detail-made-btn" onClick={onMadeItPress}>Made it?</button>
        </div>

        <div className="nmha-detail-stats">
          {cookTime > 0 && <div className="nmha-detail-stat"><b>{cookTime} min</b><span>Cook time</span></div>}
          {servings > 0 && <div className="nmha-detail-stat"><b>{servings}</b><span>Servings</span></div>}
          {calorieCount > 0 && <div className="nmha-detail-stat"><b>{calorieCount}</b><span>Calories</span></div>}
          <div className="nmha-detail-stat"><b>{likeCount}</b><span>Likes</span></div>
        </div>

        {cuisineTags.length > 0 && (
          <div className="nmha-detail-cuisine-tags">
            {cuisineTags.map((t) => (
              <span key={t.id || t.cuisine?.id} className="nmha-detail-cuisine-tag">{t.cuisine?.name}</span>
            ))}
          </div>
        )}

        {ingredients.length > 0 && (
          <section className="nmha-detail-section">
            <p className="nmha-detail-section-head">Ingredients</p>
            {ingredients.map((ing, i) => (
              <div key={i} className="nmha-detail-row">
                <span className="nmha-detail-bullet" />
                <span>{ing.amount ? `${ing.amount} ${ing.item}` : ing.item}</span>
              </div>
            ))}
          </section>
        )}

        {steps.length > 0 && (
          <section className="nmha-detail-section">
            <p className="nmha-detail-section-head">Steps</p>
            {steps.map((step, i) => (
              <div key={i} className="nmha-detail-row">
                <span className="nmha-detail-step-num">{i + 1}</span>
                <span>{step.instruction || step}</span>
              </div>
            ))}
          </section>
        )}

        {tips.length > 0 && (
          <section className="nmha-detail-section">
            <p className="nmha-detail-section-head">Tips</p>
            {tips.map((tip, i) => (
              <div key={i} className="nmha-detail-row">
                <span className="nmha-detail-bullet" />
                <span>{tip.tip_text || tip}</span>
              </div>
            ))}
          </section>
        )}

        {(post?.originalId || post?.id) && (
          <section className="nmha-detail-section" id="nmha-detail-comments">
            <p className="nmha-detail-section-head">Comments ({commentCount})</p>
            <CommentList
              postId={post.originalId || post.id}
              refreshCommentList={refreshCommentList}
              setRefreshCommentList={setRefreshCommentList}
            />
            <CommentBox
              postId={post.originalId || post.id}
              commentId=""
              setRefreshCommentList={setRefreshCommentList}
              onCommentAdded={() => setDetail((d) => (d ? { ...d, comment_count: (d.comment_count || 0) + 1 } : d))}
            />
          </section>
        )}

        {loading && <p className="nmha-loading-inline">Loading…</p>}
      </div>
    </div>
  );
};

const MadeItEntryCard = ({ entry }) => {
  const [liked, setLiked] = useState(entry?.is_like === 1);
  const [likeCount, setLikeCount] = useState(entry?.like_count || 0);
  const [commentCount, setCommentCount] = useState(entry?.comment_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [refreshCommentList, setRefreshCommentList] = useState(0);

  const name = `${entry?.user?.first_name || ''} ${entry?.user?.last_name || ''}`.trim() || 'User';
  const reactionLabel = REACTION_OPTIONS.find((r) => r.value === entry?.reaction);
  const media = entry?.media?.[0];
  const mediaUrl = media?.file_url || media?.image_path || media?.path || media?.thumb || null;
  const isVideo = media?.file_type === 2 || media?.media_type === 1;

  const toggleLike = async () => {
    const next = !liked;
    const newCount = next ? likeCount + 1 : likeCount - 1;
    setLiked(next);
    setLikeCount(newCount);
    try {
      await apiHandler(FEED_MADE_IT_LIKE, 'POST', { made_it_id: entry.id });
    } catch (_) {
      setLiked(!next);
      setLikeCount(likeCount);
    }
  };

  return (
    <div className="nmha-made-it-entry">
      <div className="nmha-made-it-entry-top">
        <Avatar url={entry?.user?.user_setting?.photo} name={name} className="nmha-pr-avatar" />
        <div className="nmha-made-it-entry-who">
          <b>{name}</b>
          <span>{formatRelativeTime(entry?.created_at)}</span>
        </div>
        {reactionLabel && (
          <span className="nmha-made-it-reaction">{reactionLabel.icon} {reactionLabel.key}</span>
        )}
      </div>
      {entry?.content && <p className="nmha-made-it-entry-text">{entry.content}</p>}
      {mediaUrl && (isVideo ? (
        <video src={mediaUrl} controls className="nmha-made-it-entry-media" />
      ) : (
        <img src={mediaUrl} alt="" className="nmha-made-it-entry-media" />
      ))}
      <div className="nmha-made-it-entry-actions">
        <span className={`nmha-pa-btn${liked ? ' liked' : ''}`} onClick={toggleLike} role="button" tabIndex={0}>
          <span className={`material-symbols-outlined${liked ? ' filled' : ''}`} style={{ fontSize: 16 }}>favorite</span> {likeCount}
        </span>
        <span className="nmha-pa-btn" onClick={() => setShowComments((v) => !v)} role="button" tabIndex={0}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chat_bubble_outline</span> {commentCount}
        </span>
      </div>
      {showComments && (
        <div className="nmha-made-it-entry-comments">
          <MadeItCommentList
            madeItId={entry.id}
            refreshCommentList={refreshCommentList}
            setRefreshCommentList={setRefreshCommentList}
            onCommentAdded={() => setCommentCount((c) => c + 1)}
          />
          <MadeItCommentBox
            madeItId={entry.id}
            commentId=""
            setRefreshCommentList={setRefreshCommentList}
            onCommentAdded={() => setCommentCount((c) => c + 1)}
          />
        </div>
      )}
    </div>
  );
};

export const MadeItListScreen = ({ post, entries, loading, onBackHome }) => (
  <div>
    <div className="nmha-detail-back-row">
      <button type="button" className="nmha-back-btn" onClick={onBackHome}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span> Back
      </button>
    </div>

    <div className="nmha-detail-wrap">
      <h1 className="nmha-detail-title">Made it ({post?.madeItCount ?? entries.length})</h1>
      {post?.title && <p className="nmha-detail-desc">{post.title}</p>}

      {loading ? (
        <p className="nmha-loading-inline">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="nmha-empty-text">No one has made this yet.</p>
      ) : entries.map((entry) => <MadeItEntryCard key={entry.id} entry={entry} />)}
    </div>
  </div>
);

// Module-level (not React state) so it survives HomeAppPage unmounting when
// the user navigates to another route (e.g. a recipe detail page) and
// remounting on the way back - restores the feed scroll position instead of
// always landing back at the top.
let cachedFeedScrollTop = 0;

const HomeAppPage = () => {
  const navigate = useNavigate();
  const userDataRaw = useSelector(userDataSelector);
  const currentUser = getCurrentUser(userDataRaw);
  const currentUserName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'You';
  const currentUserPhoto = currentUser?.user_setting?.photo || null;

  const [madeItFlow, setMadeItFlow] = useState('home');
  const [selectedPost, setSelectedPost] = useState(null);
  const [storyGroups, setStoryGroups] = useState([]);
  const [myStoryGroup, setMyStoryGroup] = useState(null);
  const [viewedStoryIds, setViewedStoryIds] = useState(() => new Set());
  const [storyViewer, setStoryViewer] = useState(null); // { groups, groupIndex } | null
  const [feedList, setFeedList] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const feedColRef = useRef(null);
  const [commentsPost, setCommentsPost] = useState(null);
  const [refreshCommentList, setRefreshCommentList] = useState(0);
  const [likesPost, setLikesPost] = useState(null);
  const [likesUserList, setLikesUserList] = useState([]);
  const [madeItListPost, setMadeItListPost] = useState(null);
  const [madeItEntries, setMadeItEntries] = useState([]);
  const [madeItListLoading, setMadeItListLoading] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState(null);
  const [showCreateStoryModal, setShowCreateStoryModal] = useState(false);

  const [topChallenge, setTopChallenge] = useState(null);

  const [circleTab, setCircleTab] = useState('followers');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [circleLoading, setCircleLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [busyFollowIds, setBusyFollowIds] = useState(new Set());

  const fetchStories = async () => {
    try {
      const [myRes, othersRes] = await Promise.all([
        apiHandler(STORIES_LIST, 'POST', { page: 1, limit: 20, mystory: 1 }),
        apiHandler(STORIES_LIST, 'POST', { page: 1, limit: 50, mystory: 0 }),
      ]);

      if (apiSucceeded(myRes)) {
        const mine = (myRes.data.data.stories.data || []).filter((d) => d?.user_post_images?.length > 0);
        const [mineGroup] = buildStoryGroups(mine);
        setMyStoryGroup(mineGroup || null);
      }

      if (apiSucceeded(othersRes)) {
        const raw = (othersRes.data.data.stories.data || []).filter((d) => d?.user_post_images?.length > 0);
        setStoryGroups(buildStoryGroups(raw));
      }
    } catch (_) { /* ignore, feed still usable without stories */ }
  };

  function isStoryGroupSeen(group) {
    return group.items.every((it) => viewedStoryIds.has(it.id));
  }

  function openStoryViewer(groups, groupIndex) {
    setStoryViewer({ groups, groupIndex });
  }

  function markStoryItemViewed(imageId) {
    if (viewedStoryIds.has(imageId)) return;
    setViewedStoryIds((prev) => new Set(prev).add(imageId));
    apiHandler(STORIES_VIEW, 'POST', { image_id: imageId }).catch((e) => console.error(e));
  }

  const fetchPosts = async () => {
    try {
      const res = await apiHandler(FEEDS_LIST, 'POST', { page: 1, limit: 10 });
      if (apiSucceeded(res)) {
        const raw = res.data.data.feeds.data || [];
        setFeedList(raw.map((d) => {
          const fullName = `${d.user.first_name || ''} ${d.user.last_name || ''}`.trim();
          const firstMedia = d.user_post_images?.[0] || null;
          const isVideo = firstMedia?.media_type === 1;
          const mediaUrl = firstMedia ? (isVideo ? (firstMedia.thumb || null) : firstMedia.image_path) : null;
          const videoUrl = isVideo ? (firstMedia.image_path || null) : null;
          return {
            id: String(d.id),
            originalId: d.id,
            userId: d.user_id,
            isOwn: currentUser?.id === d.user_id,
            author: fullName || 'User',
            avatarUrl: d.user.user_setting?.photo || null,
            authorSlug: d.user?.slug || '',
            region: [d.city, d.state].filter(Boolean).join(', '),
            time: formatRelativeTime(d.created_at),
            title: d.title || '',
            content: d.content || '',
            likes: d.like_count || 0,
            isLiked: d.is_like === 1,
            comments: d.comment_count || 0,
            madeItCount: d.made_it_count || 0,
            isFavorite: d.is_favorite === 1,
            isChallengeEntry: !!d.challenge_id,
            mediaUrl,
            isVideo,
            videoUrl,
            cookTime: d.cook_time || 0,
            servings: d.serving_for || 0,
          };
        }));
      }
    } catch (_) { /* keep prior list on transient failure */ }
    finally { setFeedLoading(false); }
  };

  const fetchLatestChallenge = async () => {
    try {
      const res = await apiHandler(LATEST_CHALLENGES, 'POST', { page: 1, limit: 5 });
      if (apiSucceeded(res)) {
        const d = res?.data?.data;
        const list = d?.challenges?.data || d?.challenges || d?.userPosts?.data || d?.posts?.data || (Array.isArray(d) ? d : []) || [];
        const now = Date.now();
        // Prefer the API's own challenge_status ("live"/"upcoming"/"past")
        // when present - expire_at alone isn't reliable since the API can
        // return it as null even for an already-ended challenge, which
        // would otherwise wrongly qualify a past challenge as "live".
        const live = list.find((c) => (
          (c?.winner_participate_id ?? 0) === 0
          && (c?.challenge_status ? c.challenge_status === 'live' : (!c?.expire_at || new Date(c.expire_at).getTime() >= now))
        ));
        if (live) setTopChallenge(live);
      }
    } catch (_) { /* challenges widget just stays hidden */ }
  };

  const fetchCircle = async () => {
    setCircleLoading(true);
    try {
      const [followersRes, followingRes] = await Promise.all([
        apiHandler(FOLLOWER_LIST, 'POST', { page: 1, limit: 20 }),
        apiHandler(FOLLOWING_LIST, 'POST', { page: 1, limit: 20 }),
      ]);
      if (apiSucceeded(followersRes)) setFollowers(followersRes?.data?.data?.followers?.data || []);
      if (apiSucceeded(followingRes)) setFollowing(followingRes?.data?.data?.followings?.data || []);
    } catch (_) { /* keep circle empty on failure */ }
    finally { setCircleLoading(false); }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await apiHandler(PEOPLE_YOU_MAY_KNOW, 'POST', { page: 1, limit: 4 });
      if (apiSucceeded(res)) setSuggestions(res?.data?.data?.users?.data || []);
    } catch (_) { /* suggestions stay empty */ }
  };

  useEffect(() => {
    fetchStories();
    fetchPosts();
    fetchLatestChallenge();
    fetchCircle();
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = feedColRef.current;
    if (!el) return undefined;
    const handleScroll = () => { cachedFeedScrollTop = el.scrollTop; };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore the scroll position once the feed has actual content to scroll
  // through - restoring while still loading (a shorter placeholder height)
  // would just clamp back to 0.
  useEffect(() => {
    if (madeItFlow === 'home' && !feedLoading && feedColRef.current) {
      feedColRef.current.scrollTop = cachedFeedScrollTop;
    }
  }, [madeItFlow, feedLoading]);

  const patchPost = (originalId, patch) => {
    setFeedList((prev) => prev.map((p) => (p.originalId === originalId ? { ...p, ...patch } : p)));
  };

  async function openLikesList(post) {
    setLikesPost(post);
    try {
      const res = await apiHandler(FEED_LIKES, 'POST', { post_id: post.originalId });
      if (apiSucceeded(res)) setLikesUserList(res?.data?.data?.userLikes || []);
    } catch (e) { console.error(e); }
  }

  async function openMadeItList(post) {
    setMadeItListPost(post);
    setMadeItEntries([]);
    setMadeItListLoading(true);
    try {
      const res = await apiHandler(FEED_MADE_IT_LIST, 'POST', {
        page: 1,
        limit: 10,
        original_recipe_id: post.originalId,
      });
      if (apiSucceeded(res)) setMadeItEntries(res?.data?.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setMadeItListLoading(false); }
  }

  async function sharePost(post) {
    const shareUrl = `${window.location.origin}/pages/recipe/${post.originalId}`;
    const shareData = { title: post.title || 'Nemonto recipe', text: post.title || 'Check out this recipe on Nemonto', url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (_) { /* user cancelled share */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        Swal.fire('Copied!', 'Link copied to your clipboard.', 'success');
      } catch (_) { /* clipboard unavailable */ }
    }
  }

  async function deletePost(post) {
    try {
      const res = await apiHandler(FEED_DELETE, 'DELETE', { post_id: post.originalId });
      if (apiSucceeded(res)) {
        setFeedList((prev) => prev.filter((p) => p.originalId !== post.originalId));
      } else {
        Swal.fire('Error', apiErrorMessage(res, 'Unable to delete post.'), 'error');
      }
    } catch (e) { console.error(e); }
  }

  async function toggleFollow(userId, { fromSuggestion } = {}) {
    if (busyFollowIds.has(userId)) return;
    setBusyFollowIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        if (fromSuggestion) {
          setSuggestions((prev) => prev.map((u) => (u.id === userId ? { ...u, is_following: u.is_following === 1 ? 0 : 1 } : u)));
        } else {
          fetchCircle();
        }
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyFollowIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  const rightRail = (
    <aside className="nmha-right-rail">
      {topChallenge && (
        <div className="nmha-rail-block">
          <Link to={`/pages/challenge/${topChallenge.id}`} className="nmha-challenge-mini">
            <span className="nmha-cm-live"><span className="nmha-dot" />LIVE NOW</span>
            <p className="nmha-cm-title">{topChallenge?.title || topChallenge?.name || topChallenge?.content || 'Weekly challenge'}</p>
            <p className="nmha-cm-meta">
              {[
                topChallenge?.participates_count ? `${topChallenge.participates_count} entries` : null,
                formatTimeLeft(topChallenge?.expire_at),
                topChallenge?.user ? `by ${[topChallenge.user.first_name, topChallenge.user.last_name].filter(Boolean).join(' ')}` : null,
              ].filter(Boolean).join(' · ')}
            </p>
            <span className="nmha-cm-btn">Enter challenge</span>
          </Link>
        </div>
      )}

      <AdRailWidget />

      <div className="nmha-rail-block">
        <div className="nmha-rail-head"><h3>Your circle</h3></div>
        <div className="nmha-rail-tabs">
          <div className={`nmha-rail-tab${circleTab === 'followers' ? ' active' : ''}`} onClick={() => setCircleTab('followers')} role="button" tabIndex={0}>
            Followers · {followers.length}
          </div>
          <div className={`nmha-rail-tab${circleTab === 'following' ? ' active' : ''}`} onClick={() => setCircleTab('following')} role="button" tabIndex={0}>
            Following · {following.length}
          </div>
        </div>

        {circleLoading ? (
          <p className="nmha-loading-text">Loading…</p>
        ) : circleTab === 'followers' ? (
          followers.length === 0 ? <p className="nmha-empty-text">No followers yet.</p> : followers.map((f) => {
            const user = f?.follower;
            const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
            const isFollowingBack = f?.is_following === 1;
            return (
              <div key={f.id} className="nmha-person-row">
                <Avatar url={user?.user_setting?.photo} name={name} className="nmha-pr-avatar" />
                <div>
                  <div className="nmha-pr-name">{name}</div>
                  <div className="nmha-pr-meta">{f?.followers_count ?? 0} followers</div>
                </div>
                <button
                  type="button"
                  className={`nmha-pr-action${isFollowingBack ? ' following' : ' follow'}`}
                  disabled={busyFollowIds.has(user?.id)}
                  onClick={() => toggleFollow(user?.id)}
                >
                  {isFollowingBack ? 'Following' : 'Follow'}
                </button>
              </div>
            );
          })
        ) : (
          following.length === 0 ? <p className="nmha-empty-text">Not following anyone yet.</p> : following.map((f) => {
            const user = f?.following;
            const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
            return (
              <div key={f.id} className="nmha-person-row">
                <Avatar url={user?.user_setting?.photo} name={name} className="nmha-pr-avatar" />
                <div>
                  <div className="nmha-pr-name">{name}</div>
                  <div className="nmha-pr-meta">{f?.followers_count ?? 0} followers</div>
                </div>
                <button
                  type="button"
                  className="nmha-pr-action following"
                  disabled={busyFollowIds.has(user?.id)}
                  onClick={() => toggleFollow(user?.id)}
                >
                  Following
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className="nmha-rail-block">
        <div className="nmha-rail-head">
          <h3>Suggested for you</h3>
          <Link to="/people-you-may-know">See all</Link>
        </div>

        {suggestions.length === 0 ? (
          <p className="nmha-empty-text">No suggestions right now.</p>
        ) : suggestions.map((u) => {
          const name = `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'User';
          const isFollowing = u?.is_following === 1;
          return (
            <div key={u.id} className="nmha-suggestion-card">
              <div className="nmha-sc-top">
                <Avatar url={u?.user_setting?.photo} name={name} className="nmha-sc-avatar" />
                <div>
                  <div className="nmha-sc-name">{name}</div>
                  <div className="nmha-sc-tag">{u?.follower_count ?? 0} followers</div>
                </div>
              </div>
              <button
                type="button"
                className={`nmha-sc-follow${isFollowing ? ' following' : ''}`}
                disabled={busyFollowIds.has(u.id)}
                onClick={() => toggleFollow(u.id, { fromSuggestion: true })}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );

  let mainContent;
  if (madeItFlow === 'entry') {
    mainContent = (
      <MadeItEntryScreen
        post={selectedPost}
        onClose={() => { setMadeItFlow('home'); fetchPosts(); }}
      />
    );
  } else if (madeItFlow === 'list') {
    mainContent = (
      <MadeItListScreen
        post={madeItListPost}
        entries={madeItEntries}
        loading={madeItListLoading}
        onBackHome={() => { setMadeItFlow('home'); setMadeItListPost(null); }}
      />
    );
  } else {
    mainContent = (
      <>
        <div className="nmha-feed-header">
          <h1>Home</h1>
          <div className="nmha-feed-tabs">
            <span className="nmha-feed-tab active">For you</span>
            <span className="nmha-feed-tab">Following</span>
            <span className="nmha-feed-tab">South Asian</span>
            <span className="nmha-feed-tab">Italian</span>
          </div>
        </div>

        <div className="nmha-feed-body">
          <Link to="/pages/post-submit" className="nmha-composer">
            <Avatar url={currentUserPhoto} name={currentUserName} className="nmha-composer-avatar" />
            <div className="nmha-composer-input">What did you cook today, {currentUserName.split(' ')[0]}?</div>
            <div className="nmha-composer-icon">📷</div>
          </Link>

          <div className="nmha-story-rail">
            <StoryBubble
              name="Your story"
              isAdd={!myStoryGroup}
              avatarUrl={myStoryGroup?.avatarUrl}
              seen={myStoryGroup ? isStoryGroupSeen(myStoryGroup) : false}
              onPress={() => (myStoryGroup
                ? openStoryViewer([myStoryGroup, ...storyGroups], 0)
                : setShowCreateStoryModal(true))}
              onAddPress={myStoryGroup ? () => setShowCreateStoryModal(true) : null}
            />
            {storyGroups.map((group, idx) => (
              <StoryBubble
                key={group.userId}
                name={group.name}
                avatarUrl={group.avatarUrl}
                seen={isStoryGroupSeen(group)}
                onPress={() => openStoryViewer(
                  myStoryGroup ? [myStoryGroup, ...storyGroups] : storyGroups,
                  myStoryGroup ? idx + 1 : idx,
                )}
              />
            ))}
          </div>

          {feedLoading ? (
            <p className="nmha-loading-text">Loading feed…</p>
          ) : feedList.length === 0 ? (
            <p className="nmha-empty-text">No posts yet. Follow someone to see their posts here.</p>
          ) : (
            feedList.flatMap((post, idx) => {
              const nodes = [
                <PostCard
                  key={post.id}
                  post={post}
                  onOpenRecipe={() => navigate(`/pages/recipe/${post.originalId}`)}
                  onProfilePress={() => {
                    if (!post.authorSlug) return;
                    navigate(`/dashboard/app/friend-profile/${post.authorSlug}`);
                  }}
                  onLikePress={() => {
                    const wasLiked = post.isLiked;
                    patchPost(post.originalId, { isLiked: !wasLiked, likes: wasLiked ? post.likes - 1 : post.likes + 1 });
                    apiHandler(FEED_LIKE, 'POST', { post_id: post.originalId }).catch(() => {
                      patchPost(post.originalId, { isLiked: wasLiked, likes: post.likes });
                    });
                  }}
                  onLikesPress={() => openLikesList(post)}
                  onCommentsPress={() => setCommentsPost(post)}
                  onMadeItCountPress={() => { setMadeItFlow('list'); openMadeItList(post); }}
                  onMadeItPress={() => { setSelectedPost(post); setMadeItFlow('entry'); }}
                  onSharePress={() => sharePost(post)}
                  onFavoritePress={() => {
                    const wasFavorite = post.isFavorite;
                    patchPost(post.originalId, { isFavorite: !wasFavorite });
                    apiHandler(ADD_FAVORITE_RECIPE, 'POST', { recipe_id: post.originalId }).catch(() => {
                      patchPost(post.originalId, { isFavorite: wasFavorite });
                    });
                  }}
                  onDelete={() => deletePost(post)}
                />,
              ];
              if ((idx + 1) % 3 === 0) {
                nodes.push(<AdFeedCard key={`ad-${idx}`} adSlot={Math.floor((idx + 1) / 3)} />);
              }
              return nodes;
            })
          )}
        </div>
      </>
    );
  }

  if (!localStorage.getItem('uzoneAuthToken')) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return (
    <div className="nmha-root">
      <div className="nmha-app">
        <main className="nmha-feed-col" ref={feedColRef}>{mainContent}</main>
        {rightRail}
      </div>

      <Modal show={!!commentsPost} onHide={() => setCommentsPost(null)} centered scrollable>
        <Modal.Header closeButton><Modal.Title>Comments</Modal.Title></Modal.Header>
        <Modal.Body>
          {commentsPost && (
            <CommentList
              postId={commentsPost.originalId}
              refreshCommentList={refreshCommentList}
              setRefreshCommentList={setRefreshCommentList}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {commentsPost && (
            <div className="w-100">
              <CommentBox
                postId={commentsPost.originalId}
                commentId=""
                setRefreshCommentList={setRefreshCommentList}
                onCommentAdded={() => patchPost(commentsPost.originalId, { comments: (commentsPost.comments || 0) + 1 })}
              />
            </div>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={!!likesPost} onHide={() => setLikesPost(null)} centered>
        <Modal.Header closeButton><Modal.Title>Likes</Modal.Title></Modal.Header>
        <Modal.Body>
          {likesUserList.length === 0 ? (
            <p className="text-muted mb-0">No likes yet.</p>
          ) : likesUserList.map((elm) => (
            <div key={elm?.id} style={{ padding: '6px 0' }}>{elm?.user?.first_name} {elm?.user?.last_name}</div>
          ))}
        </Modal.Body>
      </Modal>

      <Modal show={!!videoModalUrl} onHide={() => setVideoModalUrl(null)} centered>
        <Modal.Body style={{ padding: 0 }}>
          {videoModalUrl && <video src={videoModalUrl} controls autoPlay style={{ width: '100%' }} />}
        </Modal.Body>
      </Modal>

      {storyViewer && (
        <StoryViewerOverlay
          groups={storyViewer.groups}
          initialGroupIndex={storyViewer.groupIndex}
          currentUserId={currentUser?.id}
          onClose={() => setStoryViewer(null)}
          onItemViewed={markStoryItemViewed}
        />
      )}

      {showCreateStoryModal && (
        <CreatePostModal
          postType={"1"}
          refreshApi={fetchStories}
          createPostModalShow={showCreateStoryModal}
          setCreatePostModalShow={setShowCreateStoryModal}
          eventId={""}
        />
      )}
    </div>
  );
};

export default HomeAppPage;
